import AttachmentEditor from '@components/AttachmentEditor'
import { Image as ImageModel } from '@models/Image'
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Image,
  Space,
  Tag,
  Typography,
} from 'antd'
import { UploadFile } from 'antd/es/upload'
import { ReactNode, useEffect, useState } from 'react'
import { DEFAULT_ERROR } from '../App'

const { Title, Text } = Typography

/** Size of the photo bundled in the app, whose ratio the crop is locked to. */
const PHOTO_WIDTH = 1125
const PHOTO_HEIGHT = 432

/** What the API accepts. The shared `picture` type also lets GIFs through. */
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const REJECTED_TYPE_MESSAGE = 'Only JPEG, PNG or WebP images are accepted.'

const FIELD = 'file'

interface Props {
  title: ReactNode
  /** Helper text rendered inside the card, under the photo. */
  description?: ReactNode
  /** The photo currently stored, if any. */
  current: ImageModel | null
  /** Upload the cropped photo. A rejection keeps the picked file in place. */
  onSubmit: (file: File) => Promise<unknown>
}

const acceptedTypeRule = {
  validator: (_: unknown, value?: UploadFile[]) => {
    const file = value?.[value.length - 1]
    return !file || ACCEPTED_TYPES.includes(file.type ?? '')
      ? Promise.resolve()
      : Promise.reject(new Error(REJECTED_TYPE_MESSAGE))
  },
}

interface UploadRejection {
  message?: string
  error?: {
    response?: {
      data?: { message?: string; errors?: Record<string, string[]> }
    }
  }
}

/**
 * The API answers a refused file with Laravel's bare validation payload, which
 * SBAPI does not unwrap into `message`: read the field error from the response.
 */
const rejectionMessage = (rejection?: UploadRejection): string => {
  const data = rejection?.error?.response?.data
  return (
    data?.errors?.[FIELD]?.[0] ??
    data?.message ??
    rejection?.message ??
    DEFAULT_ERROR
  )
}

/**
 * Pick, crop and preview the photo of the membership demo tab. Nothing is sent
 * until Save: the dragger only holds the cropped file locally. The photo can be
 * replaced but never removed.
 */
const DemoTabPhotoUploader = ({
  title,
  description,
  current,
  onSubmit,
}: Props) => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>()

  const watched = Form.useWatch(FIELD, form) as UploadFile[] | undefined
  const picked = watched?.[0]
  const pickedFile =
    picked &&
    picked.status !== 'error' &&
    ACCEPTED_TYPES.includes(picked.type ?? '')
      ? (picked.originFileObj as File | undefined)
      : undefined

  const [previewUrl, setPreviewUrl] = useState<string>()
  useEffect(() => {
    if (!pickedFile) {
      setPreviewUrl(undefined)
      return
    }
    const url = URL.createObjectURL(pickedFile)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [pickedFile])

  // A new pick supersedes the error of the previous attempt.
  useEffect(() => setError(undefined), [picked?.uid])

  const onFinish = () => {
    if (!pickedFile) return

    setIsLoading(true)
    setError(undefined)
    onSubmit(pickedFile)
      .then(() => form.resetFields())
      .catch((rejection: UploadRejection | undefined) =>
        setError(rejectionMessage(rejection))
      )
      .finally(() => setIsLoading(false))
  }

  const shownUrl = previewUrl ?? current?.url

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Flex vertical className="w-full" gap={8}>
        <Space className="w-full space-between">
          <Title level={5} className="m-0">
            {title}
          </Title>
          <Button
            style={{ border: 0 }}
            type="primary"
            loading={isLoading}
            disabled={!pickedFile}
            htmlType="submit"
          >
            Save
          </Button>
        </Space>

        <Card>
          <Flex vertical gap={16}>
            <Flex vertical gap={4}>
              <Space>
                <Text strong>{previewUrl ? 'Preview' : 'Current photo'}</Text>
                {previewUrl && <Tag color="orange">Not saved yet</Tag>}
              </Space>
              {shownUrl ? (
                <Image
                  src={shownUrl}
                  alt="Membership demo tab photo"
                  width="100%"
                  style={{
                    aspectRatio: `${PHOTO_WIDTH} / ${PHOTO_HEIGHT}`,
                    objectFit: 'cover',
                    borderRadius: 8,
                  }}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No photo yet: the app shows its bundled one."
                />
              )}
            </Flex>

            {error && <Alert type="error" showIcon title={error} />}

            <AttachmentEditor
              formItem={{ name: FIELD, rules: [acceptedTypeRule] }}
              dragger={{ fileTypes: ['picture'], multiple: false }}
              image={{ width: PHOTO_WIDTH, height: PHOTO_HEIGHT }}
              hideLabel
            />

            {description && (
              <Card styles={{ body: { padding: 10 } }}>
                <Text type="secondary">{description}</Text>
              </Card>
            )}
          </Flex>
        </Card>
      </Flex>
    </Form>
  )
}

export default DemoTabPhotoUploader
